import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Stack,
  Typography,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Checkbox,
} from '@mui/material';
import { AxiosError } from 'axios';
import { enqueueSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';

import { GenerateRentLinkDto } from '@/types/dto/rent-unit.dto';

import { useSendPaymentLinkEmail } from '@/api/endpoints/payment/useSendPaymentLinkEmail';
import { useGenerateRentLink } from '@/api/endpoints/rent/useGenerateRentLink';
import { useGetPaymentBreakdown } from '@/api/endpoints/rent/useGetPaymentBreakdown';
import { useRegenerateSubscriptionLink } from '@/api/endpoints/rent/useRegenerateSubscriptionLink';
import { useGetStripeAccountStatus } from '@/api/endpoints/stripeConnect/useGetStripeAccountStatus';
import { useGetAllTenants } from '@/api/endpoints/tenant/useGetAllTenants';
import { useUpdateTenantMetadata } from '@/api/endpoints/tenant/useUpdateTenantMetadata';
import { TenantModelWithContacts } from '@/api/models/tenant';
import { PMPRoutes } from '@/config/routes';

import FormTextField from '@/components/form/FormTextField';
import { PaymentLinksContainer } from '@/components/resident/PaymentLinkCard';

interface GenerateLinkFormValues {
  tenantEmail: string;
  tenantName: string;
  noFeePayment: boolean;
}

interface GenerateSubscriptionLinkSectionProps {
  unitId: number;
  isRentDefined: boolean;
}

interface ApiErrorResponse {
  statusCode: number;
  timestamp: string;
  path: string;
  name: string;
  message: string;
  details: Record<string, unknown>;
}

// Payment links interface
interface PaymentLinks {
  achUrl: string;
  cardUrl: string;
  baseAmount?: number;
}

const GenerateSubscriptionLinkSection: React.FC<GenerateSubscriptionLinkSectionProps> = ({
  unitId,
  isRentDefined,
}) => {
  const history = useHistory();

  const { data: stripeAccountStatus, isLoading: isLoadingStatus } = useGetStripeAccountStatus(
    !!unitId
  );

  const generateLinkForm = useForm<GenerateLinkFormValues>({
    defaultValues: {
      tenantEmail: '',
      tenantName: '',
      noFeePayment: false,
    },
    mode: 'onChange',
  });

  // State for storing the primary tenant
  const [primaryTenant, setPrimaryTenant] = useState<TenantModelWithContacts | null>(null);

  // State for payment links
  const [paymentLinks, setPaymentLinks] = useState<PaymentLinks | null>(null);

  // Set up validation schema
  React.useEffect(() => {
    generateLinkForm.register('tenantEmail', {
      required: 'Email is required',
      pattern: {
        value: /^\S+@\S+\.\S+$/,
        message: 'Invalid email format',
      },
    });

    generateLinkForm.register('tenantName', {
      required: 'Full name is required',
    });
  }, [generateLinkForm]);

  // Fetch tenants for this unit
  const tenantsQuery = useGetAllTenants(
    {
      unit_id: unitId,
    },
    !!unitId
  );

  // Find the primary tenant and populate form
  useEffect(() => {
    if (tenantsQuery.data && tenantsQuery.data.length > 0) {
      // Find primary tenant
      const foundPrimaryTenant = tenantsQuery.data.find(
        tenant => tenant.metadata?.is_primary === true
      );

      if (foundPrimaryTenant) {
        // Store the primary tenant
        setPrimaryTenant(foundPrimaryTenant);

        // Find first email in contacts
        const primaryEmail =
          foundPrimaryTenant.contacts.find(contact => contact.email)?.email || '';

        // Set form values
        generateLinkForm.setValue('tenantName', foundPrimaryTenant.full_name);
        generateLinkForm.setValue('tenantEmail', primaryEmail);

        // Set payment links if they exist
        if (foundPrimaryTenant.metadata?.payment_links) {
          try {
            const parsedLinks = JSON.parse(
              foundPrimaryTenant.metadata.payment_links as string
            ) as PaymentLinks;
            setPaymentLinks(parsedLinks);
          } catch (e) {
            // Silently handle parse error without console.error
            setPaymentLinks(null);
          }
        }
      }
    }
  }, [tenantsQuery.data, generateLinkForm]);

  // State for storing error message
  const [errorMessage, setErrorMessage] = React.useState<string>(
    'Failed to generate links. Please try again.'
  );

  // Mutation for updating tenant metadata
  const updateTenantMetadataMutation = useUpdateTenantMetadata(primaryTenant?.id || 0, {
    onError: () => {
      enqueueSnackbar('Failed to save payment links to tenant record', { variant: 'error' });
    },
  });

  // Mutation for sending payment link email
  const sendPaymentLinkEmailMutation = useSendPaymentLinkEmail({
    onError: () => {
      enqueueSnackbar('Failed to send payment links email', { variant: 'error' });
    },
  });

  // State for confirmation dialog
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingFormData, setPendingFormData] = useState<GenerateLinkFormValues | null>(null);

  // State for regeneration confirmation
  const [confirmRegenerateDialogOpen, setConfirmRegenerateDialogOpen] = useState(false);

  // State for tracking subscription link regeneration
  const [isRegeneratingLink, setIsRegeneratingLink] = useState(false);

  // Add state for regeneration with no fee option
  const [regenerateWithNoFee, setRegenerateWithNoFee] = useState(true);

  // Fetch payment breakdown information
  const { data: paymentBreakdown, refetch: refetchPaymentBreakdown } = useGetPaymentBreakdown(
    unitId,
    !!unitId
  );

  // Regenerate subscription link mutation
  const regenerateSubscriptionLinkMutation = useRegenerateSubscriptionLink(unitId, {
    onSuccess: data => {
      if (data.success) {
        const links = {
          achUrl: data.achUrl,
          cardUrl: data.cardUrl,
          baseAmount: data.baseAmount,
        };

        setPaymentLinks(links);

        // Save the links to the tenant's metadata if we have a primary tenant
        if (primaryTenant) {
          const currentMetadata = primaryTenant.metadata || {};
          updateTenantMetadataMutation.mutate({
            metadata: {
              ...currentMetadata,
              payment_links: JSON.stringify(links),
              // Reset email sent status when generating new links
              payment_link_email_sent: false,
              payment_link_email_sent_at: null,
              has_active_subscription: false,
            },
          });
        }

        enqueueSnackbar(data.message, { variant: 'success' });
      } else {
        enqueueSnackbar(data.message || 'Link generation successful but no links were returned', {
          variant: data.success ? 'warning' : 'error',
        });
      }
    },
    onError: () => {
      enqueueSnackbar('Failed to generate new subscription links', { variant: 'error' });
    },
  });

  // State for email confirmation dialog
  const [emailConfirmDialogOpen, setEmailConfirmDialogOpen] = useState(false);

  // Open the email confirmation dialog
  const handleOpenSendEmailConfirmation = () => {
    if (!primaryTenant || !paymentLinks) return;

    const tenantEmail = primaryTenant.contacts.find(contact => contact.email)?.email || '';
    if (!tenantEmail) {
      enqueueSnackbar('Tenant has no email address to send to', { variant: 'error' });
      return;
    }

    setEmailConfirmDialogOpen(true);
  };

  // Handler for canceling email sending
  const handleCancelEmailSend = () => {
    setEmailConfirmDialogOpen(false);
  };

  // Handler for sending payment link email
  const handleSendPaymentLinkEmail = async () => {
    if (!primaryTenant || !paymentLinks) return;

    const tenantEmail = primaryTenant.contacts.find(contact => contact.email)?.email || '';
    if (!tenantEmail) {
      enqueueSnackbar('Tenant has no email address to send to', { variant: 'error' });
      return;
    }

    setEmailConfirmDialogOpen(false);

    try {
      await sendPaymentLinkEmailMutation.mutateAsync({
        tenantId: primaryTenant.id,
        paymentLinks,
        tenantEmail,
        tenantName: primaryTenant.full_name,
      });

      // Update tenant metadata with email sent timestamp
      if (primaryTenant) {
        const currentMetadata = primaryTenant.metadata || {};
        updateTenantMetadataMutation.mutate({
          metadata: {
            ...currentMetadata,
            payment_link_email_sent: true,
            payment_link_email_sent_at: new Date().toISOString(),
          },
        });
      }

      enqueueSnackbar('Payment links email sent successfully', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Failed to send payment links email', { variant: 'error' });
    }
  };

  // Mutation for generating link
  const generateRentLinkMutation = useGenerateRentLink(unitId, {
    onSuccess: data => {
      // Update to handle the new links response structure
      const links = {
        achUrl: data.achUrl,
        cardUrl: data.cardUrl,
        baseAmount: data.baseAmount,
      };

      setPaymentLinks(links);

      // Save the links to the tenant's metadata if we have a primary tenant
      if (primaryTenant) {
        const currentMetadata = primaryTenant.metadata || {};
        updateTenantMetadataMutation.mutate({
          metadata: {
            ...currentMetadata,
            payment_links: JSON.stringify(links),
            // Reset email sent status when generating new links
            payment_link_email_sent: false,
            payment_link_email_sent_at: null,
            has_active_subscription: false,
          },
        });
      }
    },
    onError: (error: AxiosError) => {
      const errorData = error.response?.data as ApiErrorResponse | undefined;
      if (errorData?.message) {
        setErrorMessage(errorData.message);
      } else {
        setErrorMessage('Failed to generate links. Please try again.');
      }
    },
  });

  const handleGenerateLink = generateLinkForm.handleSubmit(async data => {
    // Refetch the payment breakdown before showing dialog
    if (paymentBreakdown?.breakdown === undefined) {
      await refetchPaymentBreakdown();
    }
    // Store form data and open confirmation dialog
    setPendingFormData(data);
    setConfirmDialogOpen(true);
  });

  // Handler for confirming link generation
  const handleConfirmGeneration = () => {
    if (!pendingFormData) return;

    const linkData: GenerateRentLinkDto = {
      tenantEmail: pendingFormData.tenantEmail,
      tenantName: pendingFormData.tenantName,
      noFeePayment: pendingFormData.noFeePayment,
    };
    generateRentLinkMutation.mutate(linkData);
    setConfirmDialogOpen(false);
  };

  // Handler for canceling link generation
  const handleCancelGeneration = () => {
    setConfirmDialogOpen(false);
    setPendingFormData(null);
  };

  // Handler for regenerating link
  const handleRegenerateLink = async () => {
    // Refetch the payment breakdown before showing dialog
    if (paymentBreakdown?.breakdown === undefined) {
      await refetchPaymentBreakdown();
    }
    setConfirmRegenerateDialogOpen(true);
  };

  // Handler for confirming link regeneration
  const handleConfirmRegeneration = async () => {
    setIsRegeneratingLink(true);
    try {
      // Check if tenant exists
      if (primaryTenant) {
        // Use the new regeneration endpoint
        await regenerateSubscriptionLinkMutation.mutateAsync({
          tenantId: primaryTenant.id,
          noFeePayment: regenerateWithNoFee,
        });
      } else {
        enqueueSnackbar('No primary tenant found to generate link for', { variant: 'error' });
      }

      setConfirmRegenerateDialogOpen(false);
    } catch (error) {
      enqueueSnackbar('Error while generating new payment links', { variant: 'error' });
    } finally {
      setIsRegeneratingLink(false);
    }
  };

  // Handler for canceling link regeneration
  const handleCancelRegeneration = () => {
    setConfirmRegenerateDialogOpen(false);
  };

  // Check if email has been sent
  const emailHasBeenSent = primaryTenant?.metadata?.payment_link_email_sent === true;
  const emailSentTimestamp = primaryTenant?.metadata?.payment_link_email_sent_at;

  // Format currency
  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);

  const isLoading = isLoadingStatus || tenantsQuery.isLoading;
  const rentAmountSet = isRentDefined;
  const isOnboardingComplete = stripeAccountStatus?.onboardingComplete ?? false;

  const navigateToPayouts = () => {
    history.push(PMPRoutes.accountPayouts);
  };

  // Common description text to avoid duplication
  const descriptionText = (
    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
      Generate a payment link to begin rent collection. When ready, the tenant will be sent an email
      with this payment link where they can set up a recurring payment for the rent amount you have
      specified. Tenants can pay using credit cards, debit cards, or bank accounts.
    </Typography>
  );

  // Helper function to render send email button content without nested ternary
  const renderSendEmailButtonContent = () => {
    if (sendPaymentLinkEmailMutation.isPending) {
      return <CircularProgress size={24} sx={{ mr: 1 }} />;
    }

    return emailHasBeenSent
      ? 'Resend Rent Payment Link Email To Tenant'
      : 'Send Rent Payment Link Email To Tenant';
  };

  // Render the links box
  const renderPaymentLinks = () => {
    if (!paymentLinks) {
      return null;
    }

    return (
      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Payment Links
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          These links allow the tenant to set up recurring payments for rent. Send these links to
          the tenant, or use the "Send Rent Payment Link Email" button to automatically send them
          via email.
          {emailHasBeenSent && emailSentTimestamp && (
            <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
              Email was sent on{' '}
              {new Date(emailSentTimestamp).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: 'numeric',
                minute: 'numeric',
                hour12: true,
              })}
            </Typography>
          )}
        </Typography>

        <PaymentLinksContainer
          achUrl={paymentLinks.achUrl}
          cardUrl={paymentLinks.cardUrl}
          baseAmount={paymentLinks.baseAmount}
          onRentUpdated={() => refetchPaymentBreakdown()}
          unitId={unitId}
        />

        <Stack direction="row" spacing={2} mt={3}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenSendEmailConfirmation}
            disabled={sendPaymentLinkEmailMutation.isPending || !primaryTenant}
          >
            {renderSendEmailButtonContent()}
          </Button>
          <Button variant="outlined" color="info" onClick={handleRegenerateLink}>
            Generate New Payment Link
          </Button>
        </Stack>
      </Box>
    );
  };
  // Update to use new links rendering
  const renderContent = () => {
    if (isLoading) {
      return (
        <Stack alignItems="center" justifyContent="center" minHeight="150px">
          <CircularProgress />
        </Stack>
      );
    }

    if (!rentAmountSet) {
      return (
        <Alert severity="info">
          Please define a monthly rent amount in the section above to enable payment link
          generation.
        </Alert>
      );
    }

    if (!isOnboardingComplete) {
      return (
        <Alert
          severity="warning"
          action={
            <Button color="inherit" size="small" onClick={navigateToPayouts}>
              Go to Payouts Setup
            </Button>
          }
        >
          You need to complete your Stripe account setup before you can generate payment links.
        </Alert>
      );
    }

    return (
      <>
        {descriptionText}

        {/* Show either the existing links or the link generation form */}
        {paymentLinks ? (
          renderPaymentLinks()
        ) : (
          <Box component="form" onSubmit={handleGenerateLink} noValidate sx={{ mt: 1 }}>
            <Stack spacing={2}>
              <FormTextField
                name="tenantEmail"
                label="Tenant Email Address"
                control={generateLinkForm.control}
                required
                helperText={generateLinkForm.formState.errors.tenantEmail?.message}
                error={!!generateLinkForm.formState.errors.tenantEmail}
                textFieldProps={{
                  fullWidth: true,
                  autoComplete: 'email',
                }}
              />
              <FormTextField
                name="tenantName"
                label="Tenant Full Name"
                control={generateLinkForm.control}
                required
                helperText={generateLinkForm.formState.errors.tenantName?.message}
                error={!!generateLinkForm.formState.errors.tenantName}
                textFieldProps={{
                  fullWidth: true,
                  autoComplete: 'name',
                }}
              />
              <FormControl>
                <FormControlLabel
                  control={
                    <Checkbox
                      {...generateLinkForm.register('noFeePayment')}
                      defaultChecked={false}
                    />
                  }
                  label="Generate ACH payment link without processing fee"
                />
                <FormHelperText>
                  When checked, ACH payments will not include the standard 0.8% processing fee
                </FormHelperText>
              </FormControl>
            </Stack>
            <Button
              type="submit"
              variant="contained"
              disabled={
                generateRentLinkMutation.isPending ||
                !!generateLinkForm.formState.errors.tenantEmail ||
                !!generateLinkForm.formState.errors.tenantName
              }
              sx={{ mt: 3, mb: 2 }}
            >
              {generateRentLinkMutation.isPending ? (
                <CircularProgress size={24} />
              ) : (
                'Generate Payment Links'
              )}
            </Button>
            {generateRentLinkMutation.isError && (
              <Typography color="error" sx={{ mt: 2 }}>
                {errorMessage}
              </Typography>
            )}
          </Box>
        )}
      </>
    );
  };

  return (
    <Card>
      <CardHeader title="Generate Tenant Rent Payment Links" />
      <CardContent>
        {renderContent()}

        {/* Confirmation Dialog */}
        <Dialog
          open={confirmDialogOpen}
          onClose={handleCancelGeneration}
          aria-labelledby="confirm-link-generation-dialog-title"
          aria-describedby="confirm-link-generation-dialog-description"
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle id="confirm-link-generation-dialog-title">
            Confirm Generate Payment Links
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="confirm-link-generation-dialog-description">
              Are you sure you want to generate payment links for {pendingFormData?.tenantName}?
              These links will allow the tenant to set up recurring payments for the rent amount
              specified. They will be able to pay using credit cards, debit cards, or bank accounts.
              {pendingFormData?.noFeePayment && (
                <>
                  <br />
                  <br />
                  <strong>Note:</strong> ACH payment links will be generated without the standard
                  0.8% processing fee.
                </>
              )}
            </DialogContentText>

            {/* Payment breakdown preview */}
            {paymentBreakdown?.breakdown && (
              <Box
                sx={{ mt: 3, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
              >
                <Typography variant="subtitle1" gutterBottom>
                  Payment Breakdown Preview
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  This is what your tenant will see when they access the payment links.
                </Typography>

                <Box sx={{ mt: 2 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2">Base Rent</Typography>
                    <Typography variant="body2">
                      {formatCurrency(paymentBreakdown.breakdown.baseRent)}
                    </Typography>
                  </Stack>

                  <Divider sx={{ my: 2 }} />

                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="subtitle2">Total Monthly Payment</Typography>
                    <Typography variant="subtitle2">
                      {formatCurrency(paymentBreakdown.breakdown.total)}
                    </Typography>
                  </Stack>

                  {/* Potential Processing Fees Section */}
                  {paymentBreakdown.breakdown.potentialProcessingFees && (
                    <>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="subtitle1" gutterBottom>
                        Potential Processing Fees
                      </Typography>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Fees vary based on payment method
                      </Typography>

                      {/* Card Payments */}
                      <Box sx={{ mt: 2, mb: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Card Payments (2.9% + 30¢)
                        </Typography>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2">Domestic Cards</Typography>
                          <Typography variant="body2">
                            {formatCurrency(
                              paymentBreakdown.breakdown.potentialProcessingFees.card.domestic
                            )}
                          </Typography>
                        </Stack>
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                          sx={{ mt: 0.5 }}
                        >
                          <Typography variant="body2">+ Manual Entry</Typography>
                          <Typography variant="body2">
                            +
                            {formatCurrency(
                              paymentBreakdown.breakdown.potentialProcessingFees.card.manualEntry
                            )}
                          </Typography>
                        </Stack>
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                          sx={{ mt: 0.5 }}
                        >
                          <Typography variant="body2">+ International Cards</Typography>
                          <Typography variant="body2">
                            +
                            {formatCurrency(
                              paymentBreakdown.breakdown.potentialProcessingFees.card.international
                            )}
                          </Typography>
                        </Stack>
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                          sx={{ mt: 0.5 }}
                        >
                          <Typography variant="body2">+ Currency Conversion</Typography>
                          <Typography variant="body2">
                            +
                            {formatCurrency(
                              paymentBreakdown.breakdown.potentialProcessingFees.card
                                .currencyConversion
                            )}
                          </Typography>
                        </Stack>
                      </Box>

                      {/* Bank Payments */}
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Instant Bank Payments (2.6% + 30¢)
                        </Typography>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2">Total Fee</Typography>
                          <Typography variant="body2">
                            {formatCurrency(
                              paymentBreakdown.breakdown.potentialProcessingFees.instantBankPayment
                                .total
                            )}
                          </Typography>
                        </Stack>
                      </Box>

                      {/* ACH Direct Debit */}
                      <Box>
                        <Typography variant="subtitle2" gutterBottom>
                          ACH Direct Debit (0.8%, $5 cap)
                        </Typography>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2">Total Fee</Typography>
                          <Typography variant="body2">
                            {formatCurrency(
                              paymentBreakdown.breakdown.potentialProcessingFees.achDirectDebit
                                .total
                            )}
                          </Typography>
                        </Stack>
                      </Box>
                    </>
                  )}
                </Box>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelGeneration}>Cancel</Button>
            <Button onClick={handleConfirmGeneration} variant="contained" autoFocus>
              Generate
            </Button>
          </DialogActions>
        </Dialog>

        {/* Regeneration Confirmation Dialog */}
        <Dialog
          open={confirmRegenerateDialogOpen}
          onClose={handleCancelRegeneration}
          aria-labelledby="confirm-link-regeneration-dialog-title"
          aria-describedby="confirm-link-regeneration-dialog-description"
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle id="confirm-link-regeneration-dialog-title">
            Confirm Generate New Payment Links
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="confirm-link-regeneration-dialog-description">
              Are you sure you want to generate new payment links?
              {primaryTenant?.metadata?.has_active_subscription &&
                ' Any existing subscription will be canceled automatically.'}
              The current links will be invalidated, and any tenant who has these links will no
              longer be able to use them.
              {emailHasBeenSent && " You'll need to send the new links to the tenant again."}
              The new links will support payment via credit cards, debit cards, and bank accounts.
            </DialogContentText>

            {/* Add checkbox for no-fee option */}
            <FormControl sx={{ mt: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={regenerateWithNoFee}
                    onChange={e => setRegenerateWithNoFee(e.target.checked)}
                  />
                }
                label="Generate ACH payment link without processing fee"
              />
              <FormHelperText>
                When checked, ACH payments will not include the standard 0.8% processing fee
              </FormHelperText>
            </FormControl>

            {/* Payment breakdown preview */}
            {paymentBreakdown?.breakdown && (
              <Box
                sx={{ mt: 3, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
              >
                <Typography variant="subtitle1" gutterBottom>
                  Payment Breakdown Preview
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  This is what your tenant will see when they access the payment links.
                </Typography>

                <Box sx={{ mt: 2 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2">Base Rent</Typography>
                    <Typography variant="body2">
                      {formatCurrency(paymentBreakdown.breakdown.baseRent)}
                    </Typography>
                  </Stack>

                  <Divider sx={{ my: 2 }} />

                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="subtitle2">Total Monthly Payment</Typography>
                    <Typography variant="subtitle2">
                      {formatCurrency(paymentBreakdown.breakdown.total)}
                    </Typography>
                  </Stack>

                  {/* Potential Processing Fees Section */}
                  {paymentBreakdown.breakdown.potentialProcessingFees && (
                    <>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="subtitle1" gutterBottom>
                        Potential Processing Fees
                      </Typography>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Fees vary based on payment method
                      </Typography>

                      {/* Card Payments */}
                      <Box sx={{ mt: 2, mb: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Card Payments (2.9% + 30¢)
                        </Typography>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2">Domestic Cards</Typography>
                          <Typography variant="body2">
                            {formatCurrency(
                              paymentBreakdown.breakdown.potentialProcessingFees.card.domestic
                            )}
                          </Typography>
                        </Stack>
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                          sx={{ mt: 0.5 }}
                        >
                          <Typography variant="body2">+ Manual Entry</Typography>
                          <Typography variant="body2">
                            +
                            {formatCurrency(
                              paymentBreakdown.breakdown.potentialProcessingFees.card.manualEntry
                            )}
                          </Typography>
                        </Stack>
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                          sx={{ mt: 0.5 }}
                        >
                          <Typography variant="body2">+ International Cards</Typography>
                          <Typography variant="body2">
                            +
                            {formatCurrency(
                              paymentBreakdown.breakdown.potentialProcessingFees.card.international
                            )}
                          </Typography>
                        </Stack>
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                          sx={{ mt: 0.5 }}
                        >
                          <Typography variant="body2">+ Currency Conversion</Typography>
                          <Typography variant="body2">
                            +
                            {formatCurrency(
                              paymentBreakdown.breakdown.potentialProcessingFees.card
                                .currencyConversion
                            )}
                          </Typography>
                        </Stack>
                      </Box>

                      {/* Bank Payments */}
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Instant Bank Payments (2.6% + 30¢)
                        </Typography>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2">Total Fee</Typography>
                          <Typography variant="body2">
                            {formatCurrency(
                              paymentBreakdown.breakdown.potentialProcessingFees.instantBankPayment
                                .total
                            )}
                          </Typography>
                        </Stack>
                      </Box>

                      {/* ACH Direct Debit */}
                      <Box>
                        <Typography variant="subtitle2" gutterBottom>
                          ACH Direct Debit (0.8%, $5 cap)
                        </Typography>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2">Total Fee</Typography>
                          <Typography variant="body2">
                            {formatCurrency(
                              paymentBreakdown.breakdown.potentialProcessingFees.achDirectDebit
                                .total
                            )}
                          </Typography>
                        </Stack>
                      </Box>
                    </>
                  )}
                </Box>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelRegeneration}>Cancel</Button>
            <Button
              onClick={handleConfirmRegeneration}
              variant="contained"
              autoFocus
              disabled={isRegeneratingLink}
            >
              {isRegeneratingLink ? <CircularProgress size={24} /> : 'Generate New Links'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Email Confirmation Dialog */}
        <Dialog
          open={emailConfirmDialogOpen}
          onClose={handleCancelEmailSend}
          aria-labelledby="confirm-email-dialog-title"
          aria-describedby="confirm-email-dialog-description"
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle id="confirm-email-dialog-title">
            Confirm Sending Payment Link Email
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="confirm-email-dialog-description">
              Are you sure you want to send a payment link email to {primaryTenant?.full_name} at{' '}
              {primaryTenant?.contacts.find(contact => contact.email)?.email}? The email will
              contain a link for ACH bank account payment option.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelEmailSend}>Cancel</Button>
            <Button
              onClick={handleSendPaymentLinkEmail}
              variant="contained"
              color="primary"
              autoFocus
              disabled={sendPaymentLinkEmailMutation.isPending}
            >
              {sendPaymentLinkEmailMutation.isPending ? (
                <CircularProgress size={24} />
              ) : (
                'Send Email'
              )}
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default GenerateSubscriptionLinkSection;
